import { call, put, select } from 'redux-saga/effects';
import _ from 'underscore';
import { requestFailed } from '~src/app/actions/RequestFailedAction';
import {
    removeFromContentSelection
} from '~src/app/actions/spaces/ContentExplorerSelectionActions';
import {
    DeleteContentAction, deleteContentSucceeded
} from '~src/app/actions/spaces/DeleteContentAction';
import { requestSpaceContents } from '~src/app/actions/spaces/RequestSpaceContentsActions';
import { siteUrlsSelector } from '~src/app/selectors/SiteUrlsSelectors';
import { selectSpaceInfo } from '~src/app/selectors/SpaceInfoSelectors';
import { SpacesSiteUrls } from '~src/app/spaces/SpacesSiteUrls';
import { SpaceInfo } from '~src/app/state/SpaceInfo';
import { Messages } from '~src/codegen/Localize';
import { Location } from '~src/common/react/Location';
import {
    ContentTypeAndID, DeleteContentsAction, IDeleteContentsResult
} from '~src/content/services/DeleteContentsAction';
import { IContentInfo } from '~src/ts/models/IContentInfo';
import { isIProjectBase } from '~src/ts/models/IProjectInfo';
import { IViewInfo } from '~src/ts/models/IViewInfo';
import { isIProjectItemInfo, ISpaceTypeInfo } from '~src/ts/models/resourceInfoParts';
import { Set } from '~src/ts/models/Set';
import { ExploreContentApi_ContentType } from '~src/ts/ServerApi/types/IContent';
import { ToasterService } from '~src/ts/services/ToasterService';

import { replaceDatasourceIdWithConnectionIdForMixedContent } from '../SpacesSagaHelpers';

export function openConfirmationDialog(contents: IContentInfo[]) {
  return DeleteContentsAction.confirmDeleteContents(contents);
}

export function* deleteContents(action: DeleteContentAction) {
  try {
    let mutatableContentCopy: IContentInfo[] = [];
    mutatableContentCopy = yield call(replaceDatasourceIdWithConnectionIdForMixedContent, action.contentArray);

    let result: IDeleteContentsResult = undefined;
    try {
       result = yield call(openConfirmationDialog, mutatableContentCopy);
    } catch(e) {
      return; // Catches the rejected dismiss-dialog promise
    }

    yield put(removeFromContentSelection(...action.contentArray));

    const currentSpaceInfo: SpaceInfo = yield select(selectSpaceInfo);

    // if we've deleted the space whose page we are currently viewing
    // navigate back to the parent project
    const isCurrentSpace = (item: IContentInfo) => {
      return (item.contentType === currentSpaceInfo.spaceType
            && item.id === currentSpaceInfo.item.id);
    };

    let workbookForSpaceAlsoDeleted: boolean =
      allViewsInWorkbookDeletedForCurrentWorkbookSpace(action.contentArray, result, currentSpaceInfo);

    const deletedItemIfInCurrentSpace: IContentInfo = _.find(action.contentArray, isCurrentSpace);

    if (!!deletedItemIfInCurrentSpace || !!workbookForSpaceAlsoDeleted) {
      const parentProjectId: string = workbookForSpaceAlsoDeleted?
        getParentProjectId(currentSpaceInfo.item) :
        getParentProjectId(deletedItemIfInCurrentSpace);

      const urls: SpacesSiteUrls = yield select(siteUrlsSelector);

      // keep these success toasts when navigating away to make sure the user knows what happened
      const toaster: ToasterService = ToasterService.instance;
      toaster.markToastsAsDoNotDismissOnLocationChange();

      if (parentProjectId) {
        Location.instance.setUrl(urls.projectUrl(parentProjectId));
      } else if(deletedItemIfInCurrentSpace.contentType === 'collection') {
        Location.instance.setUrl(urls.collectionsUrl());
      } else {
        Location.instance.setUrl(urls.exploreUrl());
      }
    } else {
      // refetch content for this space.
      yield put(requestSpaceContents());
      yield put(deleteContentSucceeded());
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.deleteFailed()));
  }
}

// exported for testing
export function allViewsInWorkbookDeletedForCurrentWorkbookSpace(
  contentArray: IContentInfo[],
  result: IDeleteContentsResult,
  currentSpaceInfo: SpaceInfo) {

  const isInWorkbookViewsSubSpace = (currentSpaceInfo.spaceType === 'workbook' && currentSpaceInfo.subspaceName === 'views');
  if(!isInWorkbookViewsSubSpace) {
    return false;
  }

  if (contentArray && contentArray.length > 0) {

    const deletedViews = contentArray.filter((item: IContentInfo<ExploreContentApi_ContentType>) => {
      return item.contentType === 'view' && result.deletedItems && arrayContainsViewWithId(result.deletedItems, item.id);
    });
    const workbooksDeleted = DeleteContentsAction.determineWorkbooksToBeDeleted((Set.fromIdItemArray(deletedViews) as Set<IViewInfo>));
    return workbooksDeleted.length === 1 && workbooksDeleted[0].id === currentSpaceInfo.item.id;
  }
  return false;
}


// exported
export function arrayContainsViewWithId(arr: ContentTypeAndID[], idToMatch: string): boolean {
  return arr.findIndex((item: ContentTypeAndID) => item.contentType === 'view' && item.id  === idToMatch) >= 0;
}

function getParentProjectId(item: IContentInfo | ISpaceTypeInfo) {
  if (item.contentType === 'project' && isIProjectBase(item)) {
    if (!item.topLevelProject) {
      return item.parentId;
    }
  }
  else if (isIProjectItemInfo(item)) {
    return item.project.id;
  }

  return undefined;
}
